import { Signal } from 'typed-signals';
import type { AppStateController } from '../../../app/state/AppStateController';
import type { ResourceController } from '../../../app/resources/ResourceController';
import type { GameAreaController } from './GameAreaController';
import { BoosterType } from '../../boosters/types';
import type { BoosterTypeController } from './boosters/types';
import type { IAPController } from '../../../app/iap/IAPController';
import type { GameSceneController } from '../../GameSceneController';
import { BoosterHammerController } from './boosters/BoosterHammerController';
import { BoosterRedrawController } from './boosters/BoosterRedrawController';
import { BoosterMoveController } from './boosters/BoosterMoveController';
import { BoosterUndoController } from './boosters/BoosterUndoController';
import type { VersusSceneController } from '../../../gameJam/VersusSceneController';

interface BoosterControllerConfig {
  controller: GameAreaController;
  gameSceneController: GameSceneController | VersusSceneController;
  state: AppStateController;
  resources: ResourceController;
  iap: IAPController | null;
}

export class BoosterController {
  public readonly onBoosterUsed = new Signal<(type: BoosterType) => void>();

  private readonly typeControllers: Record<BoosterType, BoosterTypeController>;

  private activeBoosterAction: BoosterTypeController | null = null;

  constructor(private readonly config: BoosterControllerConfig) {
    this.typeControllers = {
      [BoosterType.Hammer]: new BoosterHammerController(config.controller),
      [BoosterType.Redraw]: new BoosterRedrawController(config.controller),
      [BoosterType.Move]: new BoosterMoveController(config.controller),
      [BoosterType.Undo]: new BoosterUndoController(config.gameSceneController),
    };
  }

  public async executeBoosterAction(boosterType: BoosterType): Promise<boolean> {
    const boosterUsed = await new Promise<boolean>((resolve) => {
      this.activeBoosterAction = this.typeControllers[boosterType].execute((success: boolean) => {
        if (success) this.config.resources.useBooster(boosterType);
        this.activeBoosterAction = null;
        resolve(success);
      }, this.config.gameSceneController.tutorial);
    });

    if (boosterUsed) {
      this.onBoosterUsed.emit(boosterType);
    }

    return boosterUsed;
  }

  public hasActiveAction(): boolean {
    return !!this.activeBoosterAction;
  }

  public cancelActiveAction(): void {
    this.activeBoosterAction?.cancel();
    this.activeBoosterAction = null;
  }

  public isInstantAction(boosterType: BoosterType): boolean {
    return this.typeControllers[boosterType].isInstant;
  }

  public canExecuteBoosterAction(boosterType: BoosterType): boolean {
    const { state, resources, iap } = this.config;
    if (state.getBoosterInventoryAmount(boosterType) === 0 && !resources.canAffordBooster(boosterType) && !iap) {
      return false;
    }
    return this.typeControllers[boosterType].canExecute();
  }

  public canAffordBoosterAction(boosterType: BoosterType): boolean {
    const { state, iap } = this.config;
    return !!iap && state.getBoosterInventoryAmount(boosterType) === 0;
  }
}
